import { ACCENTS } from "../demo-v2/accents.js";
import { wallTime } from "../demo-v2/clock.js";
import { MODERATION_TARGET } from "../demo-v2/seed.js";
import type { AccentId } from "../demo-v2/types.js";
import { MenuIcon, type MenuIconName } from "./icons.js";
import { COVER, PHOTOS, photoById, shelves, type WallPhoto } from "./photos.js";
import type { TourScreensCopy } from "./planner-content.js";
import { SHOT_SRC } from "./screens.js";

const DOORS: readonly MenuIconName[] = ["qr", "monitor", "shield"];
const TARGET = photoById(MODERATION_TARGET);
const NEWEST: readonly WallPhoto[] = [...PHOTOS].reverse();
const REVIEW: readonly WallPhoto[] = [
  TARGET,
  ...NEWEST.filter((photo) => photo.id !== TARGET.id).slice(0, 6),
];
const HOURS = shelves(PHOTOS);

export interface BrandMockProps {
  readonly copy: TourScreensCopy;
  readonly invitedBy: string;
  readonly name: string;
  readonly accent: AccentId;
  readonly line: string;
  readonly typing: boolean;
}

export function BrandMock({ copy, invitedBy, name, accent, line, typing }: BrandMockProps) {
  return (
    <div class="scr scr-light" data-theme="light">
      <div class="bm">
        <p class="bm-micro">{copy.demo}</p>
        <p class="bm-title">{copy.brandTitle}</p>
        <div class="bm-preview">
          <img src={COVER} alt="" loading="lazy" />
          <span class="bm-host" style={{ borderBottomColor: line }}>
            {invitedBy} {name}
          </span>
        </div>
        <p class="bm-label">{copy.nameLabel}</p>
        <span class={typing ? "inv-input is-typing" : "inv-input"}>
          <span class="inv-typed" style={{ "--chars": name.length }}>
            {name}
          </span>
        </span>
        <p class="bm-label">{copy.colorLabel}</p>
        <div class="bm-swatches">
          {ACCENTS.map((option) => (
            <span
              key={option.id}
              class={option.id === accent ? "is-on" : undefined}
              style={{ "--swatch": option.hex }}
            />
          ))}
        </div>
        <p class="bm-label">{copy.logoLabel}</p>
        <span class="bm-file">{copy.logoButton}</span>
        <span class="bm-create">{copy.create}</span>
      </div>
    </div>
  );
}

export interface PanelMockProps {
  readonly copy: TourScreensCopy;
  readonly name: string;
  readonly eventName: string;
}

export function PanelMock({ copy, name, eventName }: PanelMockProps) {
  return (
    <div class="scr scr-light" data-theme="light">
      <div class="pm">
        <p class="bm-micro">{name}</p>
        <div class="pm-head">
          <strong>{eventName}</strong>
          <span class="pm-live">{copy.live}</span>
        </div>
        <figure class="pm-cover">
          <img src={COVER} alt="" loading="lazy" />
          <figcaption>{copy.stats}</figcaption>
        </figure>
        <div class="pm-doors">
          {DOORS.map((icon, index) => (
            <span key={icon} class={index === 0 ? "pm-door is-next" : "pm-door"}>
              <MenuIcon name={icon} size={16} />
              {copy.doors[index]}
            </span>
          ))}
        </div>
        <div class="pm-qr">
          <img src="/assets/demo/qr.svg" alt="" loading="lazy" />
          <span>DEMO 2026</span>
        </div>
        <p class="bm-label">{copy.recent}</p>
        <div class="pm-recent">
          {NEWEST.slice(0, 4).map((photo) => (
            <img key={photo.id} src={photo.small} alt="" loading="lazy" />
          ))}
        </div>
      </div>
    </div>
  );
}

export interface ModerationMockProps {
  readonly copy: TourScreensCopy;
  readonly done: boolean;
}

export function ModerationMock({ copy, done }: ModerationMockProps) {
  return (
    <div class="scr scr-light" data-theme="light">
      <div class="mm">
        <p class="mm-title">{copy.moderation}</p>
        <p class="mm-section">
          <span>{copy.hiddenLabel}</span>
          <span data-testid="tour-hidden">{done ? 1 : 0}</span>
        </p>
        <ul class="mm-list">
          {REVIEW.map((photo) => {
            const target = photo.id === TARGET.id;
            const off = done && target;
            return (
              <li key={photo.id} class={off ? "is-hidden" : target ? "is-target" : undefined}>
                <img src={photo.small} alt="" loading="lazy" />
                <span class="mm-who">
                  <strong>{photo.author}</strong>
                  <span>{wallTime(photo.at)}</span>
                </span>
                <span class="mm-button">{off ? copy.hidden : copy.hide}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export interface AlbumMockProps {
  readonly copy: TourScreensCopy;
  readonly name: string;
  readonly eventName: string;
  readonly line: string;
}

export function AlbumMock({ copy, name, eventName, line }: AlbumMockProps) {
  const shown = [
    SHOT_SRC,
    ...NEWEST.filter((photo) => photo.id !== TARGET.id).map((photo) => photo.small),
  ];
  return (
    <div class="scr scr-album">
      <div class="am">
        <p class="am-host" style={{ borderBottomColor: line }}>
          {name}
        </p>
        <p class="am-event">{eventName}</p>
        <p class="am-count" data-testid="tour-album-count">
          {shown.length} {copy.photos}
        </p>
        <div class="am-chips">
          <span class="is-on">
            {copy.all} {shown.length}
          </span>
          {HOURS.map((shelf) => (
            <span key={shelf.hour}>{shelf.hour}h</span>
          ))}
        </div>
        <div class="am-grid">
          {shown.slice(0, 15).map((src) => (
            <img key={src} src={src} alt="" loading="lazy" />
          ))}
        </div>
      </div>
    </div>
  );
}
